import React from 'react';
import { AlertTriangle, Loader2, Store, X } from 'lucide-react';

import MagasinBadge from '../../components/MagasinBadge';
import ModalPortal from '../../components/ModalPortal';
import { fmt } from './posV2Utils';

/**
 * Confirmation explicite du magasin d'encaissement avant validation POS V2.
 * Évite les erreurs de caisse M1/M2 quand la session ou le stock ne correspondent pas.
 */
const PosV2EncaissementMagModal = ({
  open,
  onClose,
  allMagasins,
  sessionMag,
  selectedMag,
  onSelectMag,
  onConfirm,
  cartStockByMag,
  crossMagasin,
  total,
  remiseAmount,
  caisseByMag,
  loadingCaisse,
  loading,
}) => {
  const candidates = (allMagasins || []).length
    ? allMagasins
    : [{ code: 'M1', name: 'Magasin 1' }, { code: 'M2', name: 'Magasin 2' }];

  const stockKeys = Object.keys(cartStockByMag || {}).filter(Boolean).sort();
  const selectedCaisseClosed = selectedMag
    && !loadingCaisse
    && caisseByMag?.[selectedMag]
    && !caisseByMag[selectedMag].is_open;

  return (
    <ModalPortal isOpen={open}>
      <div
        className="w-full max-w-lg rounded-2xl border border-white/10 bg-[#151820] shadow-2xl overflow-hidden"
        data-testid="pos-v2-encaissement-mag-modal"
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
          <h3 className="font-bold text-lg text-white flex items-center gap-2">
            <Store className="text-red-400" size={22} />
            Encaisser sur quel magasin ?
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-lg text-gray-400 hover:text-white hover:bg-white/10"
            aria-label="Fermer"
          >
            <X size={18} />
          </button>
        </div>

        <div className="p-5 space-y-4 max-h-[70vh] overflow-y-auto">
          <p className="text-sm text-gray-300">
            Choisissez le magasin où enregistrer cette vente. La caisse ouverte correspondante
            sera utilisée pour le ticket et le CA.
          </p>

          {stockKeys.length > 0 && (
            <div className="rounded-xl border border-white/10 bg-black/30 p-3 text-sm space-y-2">
              <p className="text-[10px] uppercase tracking-wide text-gray-500 font-semibold">
                Stock décrémenté par magasin
              </p>
              {stockKeys.map((code) => (
                <div key={code} className="flex items-center justify-between gap-2">
                  <MagasinBadge code={code} size="sm" />
                  <span className="text-gray-200 font-mono">{fmt(cartStockByMag[code])} €</span>
                </div>
              ))}
              {crossMagasin && (
                <p className="text-[11px] text-amber-300 flex items-start gap-1.5 pt-1">
                  <AlertTriangle size={14} className="shrink-0 mt-0.5" />
                  Panier multi-magasins : un seul encaissement, stock retiré sur chaque magasin concerné.
                </p>
              )}
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {candidates.map((mag) => {
              const code = String(mag.code || '').trim().toUpperCase();
              const active = selectedMag === code;
              const caisse = caisseByMag?.[code];
              const caisseOpen = caisse?.is_open === true;
              return (
                <button
                  key={code}
                  type="button"
                  onClick={() => onSelectMag(code)}
                  data-testid={`pos-v2-sale-mag-option-${code}`}
                  className={`p-4 rounded-xl border text-left transition-all min-h-[88px] ${
                    active
                      ? 'border-red-500/60 bg-red-950/25 ring-1 ring-red-500/30'
                      : 'border-white/10 bg-black/20 hover:border-white/20'
                  }`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-black text-xl text-white">{code}</span>
                    <MagasinBadge code={code} size="sm" />
                  </div>
                  <div className="text-xs text-gray-400 mt-1">{mag.name || mag.nom || `Magasin ${code}`}</div>
                  <div className="mt-2 text-[10px] font-semibold">
                    {loadingCaisse ? (
                      <span className="text-gray-500 inline-flex items-center gap-1">
                        <Loader2 size={10} className="animate-spin" /> Caisse…
                      </span>
                    ) : caisseOpen ? (
                      <span className="text-emerald-400">Caisse ouverte</span>
                    ) : (
                      <span className="text-red-400">Caisse fermée</span>
                    )}
                  </div>
                </button>
              );
            })}
          </div>

          <div className="rounded-xl border border-white/10 bg-black/30 p-3 text-sm text-gray-300 space-y-1">
            <div className="flex justify-between">
              <span>Session POS actuelle</span>
              <MagasinBadge code={sessionMag} size="sm" />
            </div>
            {remiseAmount > 0 && (
              <div className="flex justify-between text-emerald-400 text-xs">
                <span>Remise</span>
                <span>-{fmt(remiseAmount)} €</span>
              </div>
            )}
            <div className="flex justify-between font-bold text-white text-base pt-1 border-t border-white/10">
              <span>Total encaissement</span>
              <span className="text-red-400">{fmt(total)} €</span>
            </div>
          </div>

          {selectedMag && !loadingCaisse && caisseByMag?.[selectedMag] && !caisseByMag[selectedMag].is_open && (
            <p className="text-xs text-amber-300 bg-amber-950/30 border border-amber-500/30 rounded-lg px-3 py-2">
              La caisse {selectedMag} est fermée. Ouvrez-la avant de valider, ou choisissez un autre magasin.
            </p>
          )}
        </div>

        <div className="flex gap-3 px-5 py-4 border-t border-white/10 bg-black/20">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 min-h-[48px] rounded-xl bg-white/10 hover:bg-white/15 font-semibold text-gray-200"
          >
            Annuler
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={loading || !selectedMag || loadingCaisse || selectedCaisseClosed}
            data-testid="pos-v2-sale-mag-confirm"
            className="flex-1 min-h-[48px] rounded-xl bg-red-600 hover:bg-red-500 disabled:opacity-40 font-bold text-white"
          >
            {loading ? (
              <Loader2 className="animate-spin mx-auto" size={22} />
            ) : (
              `Encaisser sur ${selectedMag || '—'}`
            )}
          </button>
        </div>
      </div>
    </ModalPortal>
  );
};

export default PosV2EncaissementMagModal;
